import React, { useEffect, useState, useMemo, useContext, ReactNode } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { StylesConfig } from 'react-select';
import ListItem from '@mui/material/ListItem';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { NavLink } from './NavLink';
import { SwipeableDrawer, Typography } from '@mui/material';
import { formatter } from '../../utils/helpers/formatters';
import { reducerTypes } from '../../store/Product/types';
import { Item, PriceItem } from './Header.styled';
import { setPriceRangeAction } from '../../store/Product/action';
import backArrow from '../../assets/images/svg/backArrow.svg';
import Image from 'next/image';
import LocalizedLink from '../Link/localized-link';
import { LanguageContext } from '../../providers/LanguageProvider';

export const dropDownStyles: StylesConfig<any, false, any> = {
  option: (provided: any, state: any) => {
    return {
      ...provided,
      background: '#fff',
      color: '#000',
      ':active': {
        background: '#000',
        color: '#fff',
      },
      cursor: 'pointer',
      fontSize: '16px',
      lineHeight: '19px',
      fontStule: 'normal',
      fontWeight: 360,
    };
  },
  input: (provided: any) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
  placeholder: (provided: any) => ({
    ...provided,
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '19px',
    fontStule: 'normal',
    fontWeight: 360,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    width: '85px',
    border: 'none',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: 'none',
    padding: 0,
    marginLeft: '16px',
    marginBottom: '26px',
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    zIndex: 5,
    padding: '0 24px 0',
    margin: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
    background: '#fff',
    color: '#000',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    transition: 'all .2s ease',
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
  }),
};

interface SidebarListProps {
  headerItems: {
    title: string;
    path: string;
    icon: string;
  }[];
  toggleDrawer: (open: boolean) => () => void;
  CurrencySelectMenu?: (styles: any) => ReactNode | null;
  LanguageSelectMenu?: (styles: any) => ReactNode | null;
  setSideBar: (open: boolean) => void;
}

export const SidebarList: React.FC<SidebarListProps> = ({
  toggleDrawer,
  headerItems,
  CurrencySelectMenu,
  LanguageSelectMenu,
  setSideBar,
}) => {
  const dispatch = useAppDispatch();
  const locale = useAppSelector((st) => st.locale);
  const { selectedCurrency } = useAppSelector((st) => st.currency);
  const [allWatchesMenuOpen, setAllWatchesMenuOpen] = useState<boolean>(false);
  const { initialFilters } = useAppSelector((st) => st.products);

  const { lang } = useContext(LanguageContext);
  const messages = useMemo(() => {
    return {
      header_items: headerItems.map((item, index) => ({
        ...item,
        title: locale[`header_items_${index}`],
      })),
      header_item_sell: locale['header_items_sell'],
      header_item_trade: locale['header_items_trade'],
      header_wishlist_header: locale['header_wishlist_header'],
      header_wishlist_text: locale['header_wishlist_text'],
      header_search_placeholder: locale['header_search_placeholder'],
      categories: {
        title: locale['header_all_watches_categories_title'],
        items: [
          locale['header_all_watches_categories_item_0'],
          locale['header_all_watches_categories_item_1'],
          locale['header_all_watches_categories_item_2'],
          locale['header_all_watches_categories_item_3'],
          locale['header_all_watches_categories_item_4'],
        ],
        path: [
          '/all-watches',
          '/new-arrivals',
          '/brands',
          '/all-watches?Gender=Men%27s%20watch/Unisex',
          '/all-watches?Gender=Women%27s%20watch',
        ],
        state: [
          undefined,
          undefined,
          undefined,
          {
            state: {
              id: '',
              key: 'Gender',
              value: "Men's watch/Unisex",
            },
          },
          {
            state: {
              id: '',
              key: 'Gender',
              value: "Women's watch",
            },
          },
        ],
      },
      collections: {
        title: locale['header_all_watches_collections_title'],
        items: [
          locale['header_all_watches_collections_item_0'],
          locale['header_all_watches_collections_item_1'],
          locale['header_all_watches_collections_item_2'],
          locale['header_all_watches_collections_item_3'],
          locale['header_all_watches_collections_item_4'],
        ],
        path: [
          '/collections/Best_Sellers/79a595df-7e9e-43e6-aebe-08dab27808f0',
          `/all-watches?pageindex=0&Movement=Automatic&curr=${selectedCurrency}`,
          '/collections/Sport_Watches/daaac1f9-b545-4a2c-48fd-08dab29e9a0e',
          `/all-watches?pageindex=0&CaseMaterial=Steel&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&CaseMaterial=Platinum&curr=${selectedCurrency}`,
        ],
        state: [
          undefined,
          {
            state: {
              id: '',
              key: 'Movement',
              value: 'Automatic',
            },
          },
          undefined,
          {
            state: {
              id: '',
              key: 'CaseMaterial',
              value: 'Steel',
            },
          },
          {
            state: {
              id: '',
              key: 'CaseMaterial',
              value: 'Platinum',
            },
          },
        ],
      },
      prices: {
        title: locale['header_all_watches_prices_title'],
        items:
          lang === 'cz'
            ? [
                `${locale['header_all_watches_prices_item_0']} ${formatter(5000, selectedCurrency)}`,
                `Od ${formatter(5000, selectedCurrency)} do ${formatter(15000, selectedCurrency)}`,
                `Od ${formatter(15000, selectedCurrency)} do ${formatter(25000, selectedCurrency)}`,
                `Od ${formatter(25000, selectedCurrency)} do ${formatter(50000, selectedCurrency)}`,
                `Od ${formatter(50000, selectedCurrency)}+`,
              ]
            : [
                `${locale['header_all_watches_prices_item_0']} ${formatter(5000, selectedCurrency)}`,
                `${formatter(5000, selectedCurrency)} to ${formatter(15000, selectedCurrency)}`,
                `${formatter(15000, selectedCurrency)} to ${formatter(25000, selectedCurrency)}`,
                `${formatter(25000, selectedCurrency)} to ${formatter(50000, selectedCurrency)}`,
                `${formatter(50000, selectedCurrency)}+`,
              ],
        path: [
          `/all-watches?pageindex=0&pricemin=0&pricemax=5000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=5000&pricemax=15000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=15000&pricemax=25000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=25000&pricemax=50000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=50000&pricemax=999999&curr=${selectedCurrency}`,
        ],
        price: [
          [0, 5000],
          [5000, 15000],
          [15000, 25000],
          [25000, 50000],
          [50000, 999999],
        ],
      },
    };
  }, [headerItems, lang, locale, selectedCurrency]);

  const [sidebarItems, setSidebarItems] = useState<{ title: string; path: string; icon?: string }[]>(
    messages.header_items
  );

  useEffect(() => {
    setSidebarItems(messages.header_items);
  }, [locale, messages.header_items]);

  function renderAllWatchesMenu() {
    return (
      <div
        style={{
          width: '284px',
          height: '100%',
          background: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '29px 24px 18px',
          position: 'relative',
        }}
      >
        <Image
          src={backArrow}
          style={{ width: '12px', height: '16px', marginBottom: '17px' }}
          onClick={() => setAllWatchesMenuOpen(false)}
          alt='back arrow icon'
        />
        <Typography variant='h4' color='#191A1B'>
          {messages.categories.title}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            paddingBottom: '12px',
            borderBottom: '0.6px solid #D7D7D7',
          }}
        >
          {messages.categories.items.map((item, index) => (
            <Item key={index}>
              <LocalizedLink
                href={messages.categories.path[index]}
                onClick={() => {
                  if (!!messages.categories.state[index]) {
                    dispatch({ type: reducerTypes.GET_FILTERS, payload: initialFilters });
                  }
                  setAllWatchesMenuOpen(false);
                  setSideBar(false);
                }}
              >
                {item}
              </LocalizedLink>
            </Item>
          ))}
        </div>
        <Typography variant='h4' color='#191A1B'>
          {messages.collections.title}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            paddingBottom: '12px',
            borderBottom: '0.6px solid #D7D7D7',
          }}
        >
          {messages.collections.items.map((item, index) => (
            <Item key={index}>
              <LocalizedLink
                href={messages.collections.path[index]}
                onClick={() => {
                  if (!!messages.collections.state[index]) {
                    dispatch({ type: reducerTypes.GET_FILTERS, payload: initialFilters });
                  }
                  setAllWatchesMenuOpen(false);
                  setSideBar(false);
                }}
              >
                {item}
              </LocalizedLink>
            </Item>
          ))}
        </div>
        <Typography variant='h4' color='#191A1B'>
          {messages.prices.title}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          {messages.prices.items.map((item, index) => (
            <PriceItem key={index}>
              <LocalizedLink
                href={messages.prices.path[index]}
                onClick={() => {
                  dispatch({ type: reducerTypes.GET_FILTERS, payload: initialFilters });
                  dispatch(setPriceRangeAction(messages.prices.price[index]));
                  setAllWatchesMenuOpen(false);
                  setSideBar(false);
                }}
              >
                {item}
              </LocalizedLink>
            </PriceItem>
          ))}
        </div>
      </div>
    );
  }

  const renderSelectors = () => {
    return (
      <>
        {CurrencySelectMenu ? CurrencySelectMenu(dropDownStyles) : null}
        {LanguageSelectMenu ? LanguageSelectMenu(dropDownStyles) : null}
      </>
    );
  };

  return (
    <Box sx={{ width: 288 }} role='presentation'>
      <List style={{ paddingTop: '17px' }}>
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>{renderSelectors()}</div>
          {sidebarItems.map((item, index) => (
            <ListItem
              key={item.title + item.path}
              style={{
                position: index === 0 ? 'relative' : undefined,
                padding: '0px',
              }}
            >
              {index === 2 ? (
                <div
                  onClick={() => {
                    setAllWatchesMenuOpen(true);
                  }}
                  style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '24px',
                    marginBottom: '10px',
                    position: 'relative',
                  }}
                >
                  <Box style={{ minWidth: '24px', display: 'flex' }}>
                    <Image
                      src={item.icon ?? ''}
                      style={{ display: 'block', margin: '0 auto' }}
                      alt='icon'
                      width={24}
                      height={24}
                    />
                  </Box>
                  <Typography
                    variant='h4'
                    style={{ fontSize: '22px', lineHeight: '26px', letterSpacing: '1px', marginLeft: '18px' }}
                  >
                    {item.title}
                  </Typography>
                </div>
              ) : (
                <NavLink withIcons {...item} onClick={toggleDrawer(false)} forMobileHeader />
              )}

              {index === 2 ? (
                <div
                  onClick={() => setAllWatchesMenuOpen(true)}
                  style={{ position: 'absolute', right: 24, bottom: 10, padding: '15px', margin: '-15px' }}
                >
                  <Image
                    src={backArrow}
                    style={{
                      width: '12px',
                      height: '16px',
                      transform: 'rotate(-180deg)',
                    }}
                    alt='back arrow icon'
                  />
                </div>
              ) : null}
            </ListItem>
          ))}
          <ListItem key={'last-sb-item'}>
            <SwipeableDrawer
              transitionDuration={{ enter: 500, exit: 500 }}
              anchor={'left'}
              open={allWatchesMenuOpen}
              onClose={() => {
                setAllWatchesMenuOpen(false);
              }}
              onOpen={() => {
                setAllWatchesMenuOpen(true);
              }}
            >
              {renderAllWatchesMenu()}
            </SwipeableDrawer>
          </ListItem>
        </>
      </List>
    </Box>
  );
};
